import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Activity, Plus, AlertTriangle, TrendingUp, Moon, Brain, Zap, Heart, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import { shouldLimitForContractFixture } from '../lib/visibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  { key: 'fatigue', label: 'Fatigue', icon: Zap, color: 'text-amber-500', low: 'Exhausted', high: 'Energized' },
  { key: 'sleep_quality', label: 'Sleep', icon: Moon, color: 'text-indigo-500', low: 'Terrible', high: 'Excellent' },
  { key: 'stress', label: 'Stress', icon: Activity, color: 'text-red-500', low: 'Very stressed', high: 'Calm' },
  { key: 'muscle_soreness', label: 'Soreness', icon: Heart, color: 'text-orange-500', low: 'Very sore', high: 'No soreness' },
  { key: 'mental_readiness', label: 'Mental', icon: Brain, color: 'text-emerald-500', low: 'Not ready', high: 'Fully focused' },
];

const ScoreBar = ({ value, max = 10 }) => {
  const pct = (value / max) * 100;
  const color = value <= 4 ? 'bg-red-500' : value <= 6 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="h-2 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

// Metric slider component (defined at module level to prevent remount on every parent render)
const MetricSlider = ({ metric, value, onChange }) => {
  const Icon = metric.icon;
  const color = value <= 4 ? 'text-red-500' : value <= 6 ? 'text-amber-500' : 'text-emerald-500';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${metric.color}`} />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{metric.label}</span>
        </div>
        <span className={`text-sm font-bold ${color}`}>{value}/10</span>
      </div>
      <input
        type="range" min={1} max={10} value={value}
        onChange={e => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
        data-testid={`slider-${metric.key}`}
      />
      <div className="flex justify-between text-[10px] text-gray-400">
        <span>{metric.low}</span><span>{metric.high}</span>
      </div>
    </div>
  );
};

const WellnessMonitoring = () => {
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [checkins, setCheckins] = useState([]);
  const [flaggedAthletes, setFlaggedAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('athlete');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCheckin, setEditingCheckin] = useState(null);
  const [trendDays, setTrendDays] = useState('30');

  const emptyForm = { fatigue: 5, sleep_quality: 5, stress: 5, muscle_soreness: 5, mental_readiness: 5, notes: '' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    axios.get(`${API}/athletes`).then(r => setAthletes(r.data)).catch(() => {});
    fetchFlagged();
  }, []);

  useEffect(() => {
    if (selectedAthlete) fetchCheckins(selectedAthlete);
  }, [selectedAthlete, trendDays]);

  const fetchCheckins = async (athleteId) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/wellness-checkins`, { params: { athlete_id: athleteId, days: parseInt(trendDays) } });
      let list = Array.isArray(r.data) ? r.data : [];
      // UX guard: Admin/Staff viewing Test Player see only the 3 most recent check-ins.
      if (shouldLimitForContractFixture(athleteId) && list.length > 3) {
        list = [...list]
          .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime())
          .slice(0, 3);
      }
      setCheckins(list);
    } catch { toast.error('Failed to fetch check-ins'); }
    finally { setLoading(false); }
  };

  const fetchFlagged = async () => {
    try {
      const r = await axios.get(`${API}/wellness-checkins/flagged-athletes`);
      setFlaggedAthletes(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!selectedAthlete) { toast.error('Select an athlete first'); return; }
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.post(`${API}/wellness-checkins`, { ...form, athlete_id: selectedAthlete, date: today, source: 'staff' });
      toast.success('Check-in recorded');
      setCreateDialogOpen(false);
      setForm(emptyForm);
      fetchCheckins(selectedAthlete);
      fetchFlagged();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to create check-in');
    }
  };

  const handleUpdate = async () => {
    if (!editingCheckin) return;
    try {
      await axios.put(`${API}/wellness-checkins/${editingCheckin.id}`, form);
      toast.success('Check-in updated');
      setEditDialogOpen(false);
      setEditingCheckin(null);
      fetchCheckins(selectedAthlete);
      fetchFlagged();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this check-in?')) return;
    try {
      await axios.delete(`${API}/wellness-checkins/${id}`);
      toast.success('Deleted');
      fetchCheckins(selectedAthlete);
      fetchFlagged();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (c) => {
    setEditingCheckin(c);
    setForm({ fatigue: c.fatigue, sleep_quality: c.sleep_quality, stress: c.stress, muscle_soreness: c.muscle_soreness, mental_readiness: c.mental_readiness, notes: c.notes || '' });
    setEditDialogOpen(true);
  };

  // Trend data
  const trendData = useMemo(() => {
    if (checkins.length === 0) return null;
    const sorted = [...checkins].sort((a, b) => a.date.localeCompare(b.date));
    const avgs = {};
    METRICS.forEach(m => {
      const vals = sorted.map(c => c[m.key]);
      avgs[m.key] = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : 0;
    });
    const latest = sorted[sorted.length - 1];
    return { sorted, avgs, latest, count: sorted.length };
  }, [checkins]);

  // Metric slider component is defined at module scope (above) to prevent remounting on every render.

  const athleteName = athletes.find(a => a.id === selectedAthlete)?.full_name || '';

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="wellness-monitoring-page">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Daily Wellness Monitoring
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">Track athlete fatigue, sleep, stress, soreness & mental state</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-5">
            <TabsTrigger value="overview" className="gap-1.5" data-testid="tab-overview"><AlertTriangle className="w-3.5 h-3.5" /> Flagged Athletes</TabsTrigger>
            <TabsTrigger value="athlete" className="gap-1.5" data-testid="tab-athlete"><TrendingUp className="w-3.5 h-3.5" /> Athlete View</TabsTrigger>
          </TabsList>

          {/* ======== FLAGGED ATHLETES TAB ======== */}
          <TabsContent value="overview">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Athletes with low wellness scores in the last 7 days</p>
            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div>
            ) : flaggedAthletes.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Activity className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">No flagged athletes. All scores are healthy.</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Record daily check-ins from the Athlete View tab</p>
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="flagged-athletes-grid">
                {flaggedAthletes.map(a => (
                  <Card key={a.athlete_id} className="border-l-4 border-l-red-400 hover:shadow-sm transition-shadow cursor-pointer" onClick={() => { setSelectedAthlete(a.athlete_id); setActiveTab('athlete'); }} data-testid={`flagged-card-${a.athlete_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{a.full_name}</p>
                          <p className="text-xs text-gray-400">{a.squad} · {a.latest_date}</p>
                        </div>
                        <div className={`text-lg font-bold ${a.latest_overall <= 4 ? 'text-red-500' : a.latest_overall <= 6 ? 'text-amber-500' : 'text-emerald-500'}`}>
                          {a.latest_overall}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(a.latest_flags || []).map((f, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 text-red-600 border-red-200 dark:border-red-800">{f}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ======== ATHLETE VIEW TAB ======== */}
          <TabsContent value="athlete">
            <div className="flex gap-3 items-end mb-5 flex-wrap">
              <div className="flex-1 min-w-[250px] max-w-md">
                <SearchableAthleteSelect
                  athletes={athletes}
                  value={selectedAthlete}
                  onChange={setSelectedAthlete}
                  placeholder="Search & select athlete..."
                  label="Select Athlete"
                  data-testid="monitoring-athlete-select"
                />
              </div>
              <Select value={trendDays} onValueChange={setTrendDays}>
                <SelectTrigger className="w-32 h-9" data-testid="trend-days-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              {selectedAthlete && (
                <Button onClick={() => { setForm(emptyForm); setCreateDialogOpen(true); }} className="bg-pfap-green hover:bg-pfap-green/90 h-9" data-testid="new-checkin-button">
                  <Plus className="w-4 h-4 mr-1" /> New Check-in
                </Button>
              )}
            </div>

            {!selectedAthlete ? (
              <Card><CardContent className="py-12 text-center">
                <TrendingUp className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">Select an athlete to view their wellness data</p>
              </CardContent></Card>
            ) : loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div>
            ) : (
              <>
                {/* Summary cards */}
                {trendData && trendData.latest && (
                  <div className="grid grid-cols-5 gap-3 mb-5" data-testid="metric-summary-cards">
                    {METRICS.map(m => {
                      const val = trendData.latest[m.key];
                      const avg = trendData.avgs[m.key];
                      const Icon = m.icon;
                      return (
                        <Card key={m.key} className="text-center" data-testid={`metric-card-${m.key}`}>
                          <CardContent className="p-3">
                            <Icon className={`w-5 h-5 mx-auto mb-1 ${m.color}`} />
                            <p className={`text-xl font-bold ${val <= 4 ? 'text-red-500' : val <= 6 ? 'text-amber-500' : 'text-emerald-500'}`}>{val}</p>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500">{m.label}</p>
                            <p className="text-[10px] text-gray-400">avg {avg}</p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                {/* Trend chart (simple bar visualization) */}
                {trendData && trendData.sorted.length > 0 && (
                  <Card className="mb-5" data-testid="trend-chart">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Wellness Trend — {athleteName}</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {METRICS.map(m => (
                          <div key={m.key} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-16 text-right">{m.label}</span>
                            <div className="flex-1 flex gap-[2px]">
                              {trendData.sorted.map((c, i) => {
                                const v = c[m.key];
                                const bg = v <= 4 ? 'bg-red-400' : v <= 6 ? 'bg-amber-400' : 'bg-emerald-400';
                                return (
                                  <div key={i} className="flex-1 group relative">
                                    <div className={`rounded-sm ${bg} transition-all`} style={{ height: `${Math.max(v * 3, 4)}px` }} title={`${c.date}: ${v}/10`} />
                                  </div>
                                );
                              })}
                            </div>
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-8">{trendData.avgs[m.key]}</span>
                          </div>
                        ))}
                        <div className="flex items-center gap-3 mt-1">
                          <span className="w-16" />
                          <div className="flex-1 flex justify-between text-[9px] text-gray-400">
                            <span>{trendData.sorted[0]?.date}</span>
                            <span>{trendData.sorted[trendData.sorted.length - 1]?.date}</span>
                          </div>
                          <span className="w-8" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Check-in history */}
                <Card data-testid="checkin-history">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Check-in History ({checkins.length})</CardTitle></CardHeader>
                  <CardContent>
                    {checkins.length === 0 ? (
                      <p className="text-center text-gray-400 py-6 text-sm">No check-ins recorded yet. Click "New Check-in" to start.</p>
                    ) : (
                      <div className="space-y-2">
                        {checkins.map(c => (
                          <div key={c.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-testid={`checkin-row-${c.id}`}>
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.overall_score <= 4 ? 'bg-red-50 dark:bg-red-900/20' : c.overall_score <= 6 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'}`}>
                              <span className={`text-sm font-bold ${c.overall_score <= 4 ? 'text-red-600' : c.overall_score <= 6 ? 'text-amber-600' : 'text-emerald-600'}`}>{c.overall_score}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{c.date}</p>
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">{c.source}</Badge>
                                {(c.flags || []).map((f, i) => (
                                  <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 text-red-600 border-red-200">{f}</Badge>
                                ))}
                              </div>
                              <div className="flex gap-3 mt-1">
                                {METRICS.map(m => (
                                  <div key={m.key} className="flex items-center gap-1">
                                    <m.icon className={`w-3 h-3 ${m.color}`} />
                                    <span className={`text-xs font-medium ${c[m.key] <= 4 ? 'text-red-500' : c[m.key] <= 6 ? 'text-amber-500' : 'text-emerald-500'}`}>{c[m.key]}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`edit-checkin-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(c.id)} data-testid={`delete-checkin-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* ===== CREATE DIALOG ===== */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-md" data-testid="create-checkin-dialog">
            <DialogHeader>
              <DialogTitle>New Wellness Check-in</DialogTitle>
              <DialogDescription>{athleteName} — {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-2">
              {METRICS.map(m => (
                <MetricSlider key={m.key} metric={m} value={form[m.key]} onChange={v => setForm({ ...form, [m.key]: v })} />
              ))}
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any observations..." rows={2} data-testid="checkin-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCreate} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="submit-checkin">Record Check-in</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== EDIT DIALOG ===== */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md" data-testid="edit-checkin-dialog">
            <DialogHeader>
              <DialogTitle>Edit Check-in</DialogTitle>
              <DialogDescription>{editingCheckin?.date}</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-2">
              {METRICS.map(m => (
                <MetricSlider key={m.key} metric={m} value={form[m.key]} onChange={v => setForm({ ...form, [m.key]: v })} />
              ))}
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any observations..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleUpdate} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="update-checkin">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WellnessMonitoring;
